'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { tmdb } from '@/lib/tmdb';
import { useStore } from '@/lib/store';
import { Search, Plus, Check, Tv, Film, Star, Calendar } from 'lucide-react';
import Image from 'next/image';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface SearchDialogProps {
  isOpen?: boolean;
  open?: boolean;
  onClose: () => void;
}

export function SearchDialog({ isOpen, open, onClose }: SearchDialogProps) {
  const dialogOpen = isOpen ?? open ?? false;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState<'all' | 'tv' | 'movie'>('all');
  const { watchlist, addToWatchlist } = useStore();

  useEffect(() => {
    if (query.length > 2) {
      const delaySearch = setTimeout(() => {
        performSearch();
      }, 500);
      return () => clearTimeout(delaySearch);
    } else {
      setResults([]);
    }
  }, [query, searchType]);

  const performSearch = async () => {
    setLoading(true);
    try {
      let data;
      if (searchType === 'tv') {
        data = await tmdb.searchShows(query);
      } else if (searchType === 'movie') {
        data = await tmdb.searchMovies(query);
      } else {
        data = await tmdb.searchMulti(query);
      }

      setResults(data.results.filter((item: any) =>
        item.media_type === 'tv' || item.media_type === 'movie' ||
        (searchType !== 'all' && (searchType === 'tv' ? item.first_air_date : item.release_date))
      ));
    } catch (error) {
      toast.error('Failed to search');
    } finally {
      setLoading(false);
    }
  };

  const isInWatchlist = (id: number) => {
    return watchlist.some(item => item.id === id);
  };

  const handleAddToWatchlist = async (item: any) => {
    const isShow = item.media_type === 'tv' || item.first_air_date;
    const type = isShow ? 'tv' : 'movie';

    if (isInWatchlist(item.id)) {
      toast.info('Already in your watchlist');
      return;
    }

    try {
      if (type === 'tv') {
        // Import ContentChecker dynamically for TV shows
        const { ContentChecker } = await import('@/lib/contentChecker');

        // Fetch full show details and calculate update schedule
        const updateDetails = await ContentChecker.checkSingleShow(item.id, 0);

        const watchlistItem = {
          id: item.id,
          type: 'tv' as const,
          name: item.name || item.title,
          posterPath: item.poster_path,
          backdropPath: item.backdrop_path,
          overview: item.overview,
          watchedSeasons: [],
          lastChecked: new Date().toISOString(),
          hasNewContent: updateDetails.hasNewSeason || updateDetails.hasNewEpisodes,
          totalSeasons: updateDetails.totalSeasons || 0,
          addedAt: new Date().toISOString(),
          rating: item.vote_average,
          genres: item.genre_ids || [],
          // Intelligent update scheduling
          status: updateDetails.status,
          nextCheckDate: updateDetails.nextCheckDate,
          updateFrequency: updateDetails.updateFrequency,
          // Content details populated immediately
          newSeasonNumber: updateDetails.hasNewSeason ? updateDetails.newSeasonNumber : undefined,
          nextEpisode: updateDetails.nextEpisode,
        };

        await addToWatchlist(watchlistItem);
        toast.success(`Added "${item.name || item.title}" to your watchlist`);
      } else {
        // Movies - use simple logic
        const watchlistItem = {
          id: item.id,
          type: 'movie' as const,
          name: item.name || item.title,
          posterPath: item.poster_path,
          backdropPath: item.backdrop_path,
          overview: item.overview,
          lastChecked: new Date().toISOString(),
          hasNewContent: false,
          addedAt: new Date().toISOString(),
          rating: item.vote_average,
          genres: item.genre_ids || []
        };

        await addToWatchlist(watchlistItem);
        toast.success(`Added "${item.name || item.title}" to your watchlist`);
      }
    } catch (error) {
      toast.error('Failed to add to watchlist');
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Shows & Movies</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for a show or movie..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
            <div className="flex gap-1">
              <Button
                variant={searchType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('all')}
              >
                All
              </Button>
              <Button
                variant={searchType === 'tv' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('tv')}
              >
                <Tv className="h-3 w-3" />
              </Button>
              <Button
                variant={searchType === 'movie' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('movie')}
              >
                <Film className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[500px] space-y-2 pr-2">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-3 p-3">
                  <Skeleton className="w-16 h-24 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))
            ) : results.length > 0 ? (
              results.filter(item => item.poster_path && item.poster_path.trim() !== '').map((item) => {
                const isShow = item.media_type === 'tv' || item.first_air_date;
                const inWatchlist = isInWatchlist(item.id);

                return (
                  <div
                    key={`${item.id}-${item.media_type}`}
                    className="flex gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="relative w-16 h-24 flex-shrink-0">
                      {item.poster_path ? (
                        <Image
                          src={tmdb.getImageUrl(item.poster_path, 'w200')}
                          alt={item.name || item.title}
                          fill
                          sizes="80px"
                          className="object-cover rounded"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted rounded flex items-center justify-center">
                          {isShow ? <Tv className="h-6 w-6 text-muted-foreground" /> : <Film className="h-6 w-6 text-muted-foreground" />}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h3 className="font-semibold truncate">
                            {item.name || item.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {isShow ? 'TV Show' : 'Movie'}
                            </Badge>
                            {item.vote_average > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                                {item.vote_average.toFixed(1)}
                              </div>
                            )}
                            {(item.first_air_date || item.release_date) && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {new Date(item.first_air_date || item.release_date).getFullYear()}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {item.overview}
                          </p>
                        </div>

                        <Button
                          size="sm"
                          variant={inWatchlist ? 'secondary' : 'default'}
                          onClick={() => handleAddToWatchlist(item)}
                          disabled={inWatchlist}
                        >
                          {inWatchlist ? (
                            <>
                              <Check className="h-3 w-3 mr-1" />
                              Added
                            </>
                          ) : (
                            <>
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : query.length > 2 ? (
              <div className="text-center py-8 text-muted-foreground">
                No results found for "{query}"
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Start typing to search...
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}